import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { ALL_GYMS, GYM_STATES } from '../../../lib/hyroxGymData';
import GymHeatMap from '../../../components/GymHeatMap';
import GymOfTheMonth from '../../../components/GymOfTheMonth';
import TopRatedGyms from '../../../components/TopRatedGyms';
import { SubmitGymForm } from '../SubmitGymForm';
import { GymFinder } from '../GymFinder';
import { HYROX_CITIES } from '../data';

export const GymsSection = ({ mapSelectedState, setMapSelectedState }) => {
  const scrollToFinder = () => {
    const el = document.getElementById('gym-finder-anchor');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="gyms" className="bg-[#050505] section-spacing" data-testid="hyrox-gyms-section">
      <div className="container-ninja">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#e8ff00] text-xs uppercase tracking-wider font-heading mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Gym Finder
          </p>
          <h2 className="text-3xl md:text-4xl text-white font-heading mb-4">Find the Best HYROX Gym Near You</h2>
          <p className="text-zinc-400 mb-6 max-w-2xl">
            We&apos;ve compiled the most comprehensive HYROX gym directory in the USA — {ALL_GYMS.length} gyms across {GYM_STATES.length} states. Find the perfect gym for your training.
          </p>

          <Link
            to="/hyrox/near-me"
            className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-[#111] border border-[#e8ff00]/30 hover:border-[#e8ff00] p-5 mb-8 transition-colors"
            data-testid="near-me-banner-link"
          >
            <div className="flex items-center gap-3">
              <MapPin className="w-6 h-6 text-[#e8ff00] shrink-0" />
              <div>
                <h3 className="font-heading text-lg text-white leading-tight">HYROX Near Me — Instant Local Finder</h3>
                <p className="text-zinc-500 text-sm">Use your location to rank all {ALL_GYMS.length} gyms by distance in one tap.</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 text-[#e8ff00] font-mono text-xs tracking-wider uppercase group-hover:gap-3 transition-all shrink-0">
              Find Gyms Near Me →
            </span>
          </Link>

          <SubmitGymForm />

          <div className="mb-10" data-testid="city-directories-top">
            <h3 className="text-white font-heading text-2xl mb-2">City Gym Directories</h3>
            <p className="text-zinc-500 text-sm mb-4">
              Our most thorough coverage — deep-dive guides with full profiles, videos, pricing, class schedules and maps for each city:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {HYROX_CITIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/blogs/article/${c.slug}`}
                  className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#e8ff00]/40 p-4 transition-all"
                  data-testid={`city-${c.city.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-3.5 h-3.5 text-[#e8ff00]" />
                    <span className="text-white text-sm font-heading group-hover:text-[#e8ff00] transition-colors">{c.city}</span>
                  </div>
                  <span className="text-zinc-600 text-xs">{c.state}</span>
                </Link>
              ))}
            </div>
          </div>

          <GymOfTheMonth onViewOnMap={(stateCode) => { setMapSelectedState(stateCode); scrollToFinder(); }} />
          <TopRatedGyms onGymClick={(stateCode) => { setMapSelectedState(stateCode); scrollToFinder(); }} />
          <GymHeatMap onStateClick={(code) => { setMapSelectedState(code); scrollToFinder(); }} />

          <div id="gym-finder-anchor" />
          <GymFinder externalState={mapSelectedState} onExternalStateConsumed={() => setMapSelectedState('')} />
        </div>
      </div>
    </section>
  );
};
